import React, { useMemo, useState } from 'react';
import { BREAKPOINTS, Col, Container, Flex } from '@/ui/layout';
import { Body1Light, Body2Light, Text, Title } from '@/ui/typography';
import { COLORS } from '@/utils/static/colors';
import {
  ENABLED_DOC_STATUSES_ADMIN,
  ENABLED_DOC_TO_VIEW_STATUSES_ADMIN,
  ENABLED_STATUSES_INVESTOR,
  STATUS_COLOR,
  STATUS_DISPLAY
} from '@/utils/static/kyc';
import { dateString, downloadFileByLink, getFileSrc } from '@/utils/helpers/common';
import { ExternalLink } from '@/ui/atoms/Link';
import { useMutation } from '@tanstack/react-query';
import { kycApi } from '@/api/kyc';
import { UploadDocumentsBox } from '@/components/common/InputBoxes/UploadDocuments';
import styled from 'styled-components';
import { entitiesActions } from '@/store/entities';
import { adminKYCApi } from '@/api/admin/kyc';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@/components/common/atoms';
import { LogOutButton } from '@/components/common/molecules';
import { Modal as ANTDModal, Input, Typography, Popconfirm } from 'antd';
import { ReactComponent as NewStatusIcon } from '@/assets/icons/status/new.svg';
import { ReactComponent as PassedStatusIcon } from '@/assets/icons/status/passed.svg';
import { ReactComponent as InReviewStatusIcon } from '@/assets/icons/status/in-review.svg';
import { ReactComponent as InProcessStatusIcon } from '@/assets/icons/status/in-process.svg';
import { ReactComponent as RejectedStatusIcon } from '@/assets/icons/status/rejected.svg';
import { ReactComponent as DraftStatusIcon } from '@/assets/icons/status/draft.svg';
import { useTranslation } from 'react-i18next';
import { Checkbox, Button as AntdButton } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router';
import { usePopups } from '@/hooks';

export const DocRequestView = ({
  isFetching,
  data,
  refetch,
  requestId,
  investorId,
  viewMode = 'investor',
  approveRequest,
  rejectRequest,
  referralView = false,
  referralToken
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const investor = useSelector((state) => state.investor);
  const [addCommentToId, setAddCommentToId] = useState(-1);
  const [modalVisible, setModalVisible] = useState(false);
  const requestInfo = useMemo(() => {
    if (data && data.data) {
      const { kycDocuments, ...requestObj } = data.data;
      return {
        ...requestObj,
        files:
          kycDocuments && kycDocuments.length > 0
            ? kycDocuments.map((file) => ({
                id: file.id,
                comment: file.comments[0] ? file.comments[0].comment : '',
                file: getFileSrc(file.documentURL),
                documentName: file.documentName,
                onlyAdmin: file.onlyAdmin
              }))
            : [{}]
      };
    }

    return {};
  }, [data]);

  const onBack = () => {
    viewMode === 'admin'
      ? navigate(`/investor/${investorId}/documents`)
      : navigate('/profile/documents');
  };

  const currentDocId = useMemo(() => {
    try {
      return requestInfo.files[0]?.id;
    } catch (e) {}

    return null;
  }, [requestInfo]);

  const STATUS_ICON = {
    PENDING: <NewStatusIcon width={16} height={16} />,
    DRAFT: <DraftStatusIcon width={16} height={16} />,
    PROCESSING: <InReviewStatusIcon width={16} height={16} />,
    QUESTION_UNACCEPTED: <InProcessStatusIcon width={16} height={16} />,
    UNACCEPTED: <RejectedStatusIcon width={16} height={16} />,
    ACCEPTED: <PassedStatusIcon width={16} height={16} />
  };
  const boxStatus = data?.data?.status;

  return (
    <>
      {!isFetching && requestInfo && (
        <>
          <HeaderRow $paddingY={24} $paddingX={44} justify='space-between'>
            <Container $maxWidth={210} $wide>
              <Button
                type='text'
                size='large'
                text={t('Back to Documents')}
                icon={<ArrowLeftOutlined />}
                onClick={onBack}
              />
            </Container>

            <LogOutButton />
          </HeaderRow>

          <StyledMainRow
            $paddingTop={60}
            $paddingX={16}
            justify={'center'}
            gap={24}
            align='flex-start'
            wrap
          >
            <StyledBlockDescription>
              <StyledStatusRow align={'center'} gap={24}>
                <Title level={2}>{requestInfo.kycTypeDocument?.title}</Title>

                <Flex align={'center'} gap={8} $paddingTop={4}>
                  {STATUS_ICON[boxStatus]}

                  <Text $fontWeight={600} $color={STATUS_COLOR[boxStatus]}>
                    {requestInfo.status ? t(`${STATUS_DISPLAY[requestInfo.status]}`) : '-'}
                  </Text>
                </Flex>
              </StyledStatusRow>

              <Container $marginTop={16} $wide>
                <Typography.Paragraph>
                  {requestInfo.kycTypeDocument?.descriptions}
                </Typography.Paragraph>
              </Container>

              <Container $marginTop={32}>
                <Text $fontSize={20} $fontWeight={600}>
                  {t('Details')}
                </Text>
              </Container>

              <Grid $marginTop={20}>
                <Text $fontSize={14} $color={COLORS.greyText2}>
                  {t('Created date')}:
                </Text>

                <Text>{requestInfo.created ? dateString(new Date(requestInfo.created)) : '-'}</Text>

                <Text $fontSize={14} $color={COLORS.greyText2}>
                  {t('Updated date')}:
                </Text>

                <Text>{requestInfo.updated ? dateString(new Date(requestInfo.updated)) : '-'}</Text>

                <Text $fontSize={14} $color={COLORS.greyText2}>
                  {t('Expiration date')}:
                </Text>

                <Text>
                  {requestInfo.expiration ? dateString(new Date(requestInfo.expiration)) : '-'}
                </Text>

                {requestInfo.onlyAdmin && (
                  <>
                    <Text $fontSize={14} $color={COLORS.greyText2}>
                      {t('Only admin')}:
                    </Text>
                    <Text>{'true'}</Text>
                  </>
                )}
              </Grid>
            </StyledBlockDescription>

            <StyledContainer $paddingLeft={32}>
              <Container>
                <Text $fontWeight={600} $fontSize={20}>
                  {t('Attached file')}
                </Text>

                {requestInfo.files.map((file, index) => (
                  <Flex
                    $margin={-16}
                    $marginBottom={8}
                    $marginTop={8}
                    gap={12}
                    justify={'flex-start'}
                    align={'center'}
                  >
                    <Body1Light $fontWeight={400}>
                      {file.file ? (
                        <>
                          <ExternalLink
                            onClick={() =>
                              downloadFileByLink(file.file.split('/').pop() ?? 'file', file.file)
                            }
                            external
                            fontSize={'14px'}
                            shortened
                          >
                            {t('File')}
                            {requestInfo.files.length - index}
                            {file.documentName && ` (${file.documentName})`}
                          </ExternalLink>
                          {viewMode === 'admin' && file.onlyAdmin && <> {t('(Only admin)')}</>}
                        </>
                      ) : (
                        <Text $fontSize={14} $color={COLORS.greyText2}>
                          {t('Upload a file(s) to make it appear')}
                        </Text>
                      )}
                    </Body1Light>
                    <Col $auto>
                      <Flex align={'center'}>
                        {file.comment && (
                          <Body2Light $color={COLORS.red}>{file.comment}</Body2Light>
                        )}
                        {viewMode === 'admin' && file.file && !file.comment && (
                          <Button
                            text={t('New comment')}
                            onClick={() => setAddCommentToId(file.id)}
                            size={'small'}
                          />
                        )}
                      </Flex>
                    </Col>
                  </Flex>
                ))}

                {!referralView && (
                  <Flex $marginTop={16} vertical>
                    <Container $marginTop={12} style={{ textAlign: 'center' }}>
                      <Button
                        block={false}
                        type='filled'
                        size='large'
                        text={t('Upload file')}
                        onClick={() => setModalVisible(true)}
                        disabled={
                          (viewMode === 'admin' &&
                            requestInfo.status &&
                            !ENABLED_DOC_TO_VIEW_STATUSES_ADMIN[requestInfo.status]) ||
                          (viewMode !== 'admin' &&
                            (investor.accountStatus !== 'ACTIVE' ||
                              (requestInfo.status &&
                                !ENABLED_STATUSES_INVESTOR[requestInfo.status])))
                        }
                      />
                    </Container>

                    {viewMode === 'admin' && (
                      <Flex $margin={-8} gap={10} $marginTop={25}>
                        <Col $auto>
                          <Button
                            text={t('Approve')}
                            onClick={() => approveRequest(currentDocId)}
                            mode={'strong'}
                            size={'small'}
                            disabled={!ENABLED_DOC_STATUSES_ADMIN[requestInfo.status]}
                          />
                        </Col>
                        <Col $auto>
                          <Button
                            text={t('Reject')}
                            onClick={() => rejectRequest(currentDocId)}
                            size={'small'}
                            disabled={!ENABLED_DOC_STATUSES_ADMIN[requestInfo.status]}
                          />
                        </Col>
                      </Flex>
                    )}
                  </Flex>
                )}
              </Container>
            </StyledContainer>
            {!referralView && (
              <UploadFileModal
                visible={modalVisible}
                onClose={() => setModalVisible(false)}
                refetchRequest={refetch}
                requestId={requestId}
                adminView={viewMode === 'admin'}
                referralView={referralView}
                referralToken={referralToken}
                investorId={investorId}
              />
            )}
            {!referralView && (
              <AddCommentModal
                visible={addCommentToId !== -1}
                onClose={() => setAddCommentToId(-1)}
                refetchRequest={refetch}
                docId={addCommentToId}
              />
            )}
          </StyledMainRow>
        </>
      )}
    </>
  );
};

const AddCommentModal = ({ visible, onClose, docId, refetchRequest }) => {
  const { t } = useTranslation();
  const { showErrorToast, hideLoader } = usePopups();
  const dispatch = useDispatch();
  const [comment, setComment] = useState('');
  const [btnDisabled, setBtnDisabled] = useState(false);

  const addDocComment = async () => {
    try {
      setBtnDisabled(true);
      dispatch(entitiesActions.showLoader('Saving...'));
      await adminKYCApi.addRequestComment({ kycDocumentId: docId, comment });
      await refetchRequest();
      onClose();
    } catch (e) {
      console.error(e);
      showErrorToast(e, true);
    } finally {
      hideLoader();
      setBtnDisabled(false);
    }
  };

  return (
    <ANTDModal
      open={visible}
      onCancel={onClose}
      title={t('Add comment')}
      footer={[
        <AntdButton key='back' onClick={onClose}>
          {t('Cancel')}
        </AntdButton>,
        <AntdButton type='primary' onClick={() => addDocComment()} disabled={btnDisabled}>
          {t('Add')}
        </AntdButton>
      ]}
    >
      <Input label={t('Comment')} value={comment} onChange={(e) => setComment(e.target.value)} />
    </ANTDModal>
  );
};
const UploadFileModal = ({
  visible,
  onClose,
  requestId,
  refetchRequest,
  adminView,
  investorId,
  referralView,
  referralToken
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { showErrorToast } = usePopups();
  const [files, setFiles] = useState([]);
  const [btnDisabled, setBtnDisabled] = useState(false);
  const { mutateAsync } = useMutation({
    mutationFn: adminView ? adminKYCApi.uploadDocument : kycApi.uploadDocument
  });
  const [onlyAdmin, setOnlyAdmin] = useState(false);

  const onUpload = async () => {
    try {
      setBtnDisabled(true);
      if (!files || files.length === 0) return;
      const MAX_FILE_SIZE = 10 * 1024 * 1024;
      const toUpload = files.slice(0, 5);
      for (const f of toUpload) {
        if (f.size > MAX_FILE_SIZE) {
          showErrorToast(t('File is too large. Maximum size is 10MB.'));
          continue;
        }
        await mutateAsync({
          requestDocumentId: requestId,
          file: f,
          onlyAdmin: onlyAdmin,
          ...(adminView && { investorId }),
          ...(referralView && { token: referralToken })
        });
      }
      await refetchRequest();
      onClose();
      setFiles([]);
    } catch (e) {
      console.error(e);
      if (e?.response?.status === 413) {
        showErrorToast(t('File is too large. Please reduce file size and try again.'));
      } else {
        showErrorToast(t('Something went wrong'));
      }
    } finally {
      setBtnDisabled(false);
    }
  };

  return (
    <ANTDModal open={visible} onCancel={onClose} footer={null}>
      <div style={{ marginTop: '20px', marginBottom: '15px' }}>
        <UploadDocumentsBox
          docs={files}
          multiple
          setDocs={(newDocs) => {
            const arr = Array.isArray(newDocs) ? newDocs : [newDocs];
            const MAX_FILE_SIZE = 10 * 1024 * 1024;
            const valid = arr.filter((f) => {
              if (f.size > MAX_FILE_SIZE) {
                dispatch(
                  entitiesActions.addToastError(t('File is too large. Maximum size is 10MB.'))
                );
                return false;
              }
              return true;
            });
            const limited = valid.slice(0, 5);
            setFiles(limited);
          }}
          buttonProps={{
            label: t('Add files')
          }}
        />
      </div>

      {adminView && (
        <Body2Light>
          <Checkbox
            name='OnlyAdmin'
            value={onlyAdmin}
            checked={onlyAdmin}
            onChange={(e) => setOnlyAdmin(e.target.checked)}
          >
            {t('Only Admin')}
          </Checkbox>
        </Body2Light>
      )}

      <Container $marginTop={24} style={{ textAlign: 'center' }}>
        <Button
          text={t('Upload file')}
          onClick={onUpload}
          type='primary'
          disabled={!files || files.length === 0 || btnDisabled}
        />
      </Container>
    </ANTDModal>
  );
};

const HeaderRow = styled(Flex)`
  background: #f6f9ff;
  box-shadow: 0px 4px 14px 0px rgba(0, 0, 0, 0.1);
  @media (max-width: ${BREAKPOINTS.md}px) {
    padding-left: 16px;
    padding-right: 16px;
  }
`;

const StyledContainer = styled(Container)`
  height: fit-content;
  border-left: 2px solid #d9d9d9;
  flex-shrink: 0;
  flex-grow: 1;

  @media (max-width: ${BREAKPOINTS.md}px) {
    border-left: none;
    padding-left: unset;
  }
`;

const StyledMainRow = styled(Flex)`
  flex-direction: row;
  flex-wrap: nowrap;
  padding: 60px;

  @media (max-width: ${BREAKPOINTS.md}px) {
    flex-direction: column;
    flex-wrap: wrap;
    padding: 40px 20px;
  }
`;

const Grid = styled(Container)`
  display: grid;
  flex-direction: row;
  grid-template-columns: max-content max-content;
  row-gap: 18px;
  column-gap: 12px;
`;

const StyledStatusRow = styled(Flex)`
  @media (max-width: ${BREAKPOINTS.md}px) {
    row-gap: 16px;
  }
`;

const StyledBlockDescription = styled.div`
  flex-shrink: 1;
  flex-grow: 0;
  flex-basis: 70%;
`;
